import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getFirstNameErrors, getLastNameErrors } from "../helpers/helpers.tsx";
import {
  getGroups,
  getHeadquarters,
  getGroupsInHeadquarter,
  getCountries,
  authedFetch,
} from "../helpers/requests.tsx";
import { User, UserRole } from "../helpers/interfaces.tsx";
import { useTranslation } from "react-i18next";

type ChangeUserDataModal = {
  setShowChangeDataModal: React.Dispatch<React.SetStateAction<boolean>>;
  user: User;
  handleChangeData: (updatedUser: User) => void;
  adminView?: boolean;
  headquarterView?: boolean;
  groupView?: boolean;
};

const ChangeUserDataModal: React.FC<ChangeUserDataModal> = ({
  setShowChangeDataModal,
  user,
  handleChangeData,
  adminView = false,
  headquarterView = false,
  groupView = false,
}) => {
  const { headquarterId } = useParams();
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newGroupId, setNewGroupId] = useState("");
  const [groupName, setGroupName] = useState("");
  const [groupCountry, setGroupCountry] = useState("");
  const [newHeadquarterId, setNewHeadquarterId] = useState("");
  const [headquarterName, setHeadquarterName] = useState("");
  const [newRole, setNewRole] = useState<UserRole>(UserRole.Guest);
  const [initialRole, setInitialRole] = useState<UserRole>(UserRole.Guest);
  const [newPreferredLanguage, setNewPreferredLanguage] = useState("EN");

  const [groups, setGroups] = useState<any[]>([]);
  const [headquarters, setHeadquarters] = useState<any[]>([]);
  const [countries, setCountries] = useState<any[]>([]);

  const [saveDataSubmitted, setSaveDataSubmitted] = useState(false);

  const { t, i18n } = useTranslation();

  useEffect(() => {
    const fetchData = async () => {
      let groupsList = [];
      if (adminView) {
        groupsList = await getGroups();
        setGroups(groupsList);

        const headquarterList = await getHeadquarters();
        setHeadquarters(headquarterList);
      } else if (headquarterView) {
        groupsList = await getGroupsInHeadquarter(headquarterId);
        setGroups(groupsList);
      }

      const countryList = await getCountries();
      setCountries(countryList);
    };

    fetchData();

    setNewFirstName(user.firstName);
    setNewLastName(user.lastName);
    setNewGroupId(user.groupId);
    setGroupName(user.groupName);
    setGroupCountry(user.groupCountryCode);
    setNewHeadquarterId(user.headquarterId);
    setHeadquarterName(user.headquarterName);
    setNewRole(user.role);
    setInitialRole(user.role);
    setNewPreferredLanguage(user.preferredLanguage || "EN");
  }, []);

  const updateUserData = async (
    userId: string,
    newFirstName: string,
    newLastName: string,
    newGroupId: string,
    newHeadquarterId: string,
    newRole: UserRole | null,
    newPreferredLanguage: string
  ) => {
    try {
      const requestBody: any = {
        userId,
        newFirstName,
        newLastName,
        newGroupId,
        newHeadquarterId,
        newPreferredLanguage,
      };

      if (newRole && newRole !== initialRole) {
        requestBody.newRole = newRole;
      }

      await authedFetch(`api/requests/updateUserData`, {
        method: "POST",
        body: JSON.stringify(requestBody),
      });
    } catch (error) {
      console.error("Error update user data:", error);
      throw new Error("An error occurred while update user data");
    }
  };

  const handleRoleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const role = e.target.value as UserRole;
    setNewRole(role);
    if (role === UserRole.User || role === UserRole.GroupAdmin) {
      setNewHeadquarterId("");
    } else if (role === UserRole.HeadquarterAdmin) {
      setNewGroupId("");
      setGroupName("");
      setNewHeadquarterId(headquarterId);
    } else if (role === UserRole.Guest) {
      setNewGroupId("");
      setGroupName("");
      setNewHeadquarterId("");
      setHeadquarterName("");
    }

    setSaveDataSubmitted(false);
  };

  const handleSaveDataClick = async () => {
    setSaveDataSubmitted(true);

    if (getFirstNameErrors(newFirstName, t) || getLastNameErrors(newLastName, t)) {
      return;
    }

    if (newRole === UserRole.User || newRole === UserRole.GroupAdmin) {
      if (newGroupId === "") return;
    } else if (newRole === UserRole.HeadquarterAdmin) {
      if (newHeadquarterId === "") return;
    }

    setShowChangeDataModal(false);

    await updateUserData(
      user.id,
      newFirstName,
      newLastName,
      newGroupId,
      newRole !== UserRole.HeadquarterAdmin &&
        user.groupId &&
        user.headquarterId
        ? ""
        : newHeadquarterId,
      newRole !== initialRole ? newRole : null,
      newPreferredLanguage
    );

    const updatedUser: User = {
      id: user.id,
      firstName: newFirstName,
      lastName: newLastName,
      email: user.email,
      groupId: newGroupId,
      groupName: groupName,
      groupCountryCode: groupCountry,
      headquarterId: newHeadquarterId,
      headquarterName: headquarterName,
      role: newRole,
      isStreaming: user.isStreaming,
      preferredLanguage: newPreferredLanguage,
    };

    handleChangeData(updatedUser);
  };

  return (
    <div className="confirmation-modal">
      <div className="modal-content">
        <div style={{ textAlign: "center", fontSize: "1.3em" }}>{t("Edit_User")}</div>
        <div className="mb-3" style={{ marginTop: "2vh" }}>
          <label
            htmlFor="firstNameInput"
            style={{
              marginLeft: "20px",
              color: "#404040",
              fontWeight: "bold",
            }}
          >
            {t("First_Name")}
          </label>
          <input
            type="text"
            id="firstNameInput"
            className="form-control"
            value={newFirstName}
            onChange={(e) => {
              setNewFirstName(e.target.value);
              setSaveDataSubmitted(false);
            }}
            style={{
              backgroundColor: "#CCCCCC",
              borderRadius: "20px",
              color: "#404040",
              height: "5vh",
              maxHeight: "40px",
              fontSize: "1em",
              paddingLeft: "20px",
            }}
          />
          <label
            htmlFor="firstNameInput"
            style={{
              marginLeft: "20px",
              color: "#A63939",
              fontSize: "0.8em",
              visibility:
                saveDataSubmitted && getFirstNameErrors(newFirstName, t)
                  ? "visible"
                  : "hidden",
            }}
          >
            {getFirstNameErrors(newFirstName, t)}
          </label>
        </div>
        <div className="mb-3" style={{ marginTop: "2vh" }}>
          <label
            htmlFor="lastNameInput"
            style={{
              marginLeft: "20px",
              color: "#404040",
              fontWeight: "bold",
            }}
          >
            {t("Last_Name")}
          </label>
          <input
            type="text"
            id="lastNameInput"
            className="form-control"
            value={newLastName}
            onChange={(e) => {
              setNewLastName(e.target.value);
              setSaveDataSubmitted(false);
            }}
            style={{
              backgroundColor: "#CCCCCC",
              borderRadius: "20px",
              color: "#404040",
              height: "5vh",
              maxHeight: "40px",
              fontSize: "1em",
              paddingLeft: "20px",
            }}
          />
          <label
            htmlFor="lastNameInput"
            style={{
              marginLeft: "20px",
              color: "#A63939",
              fontSize: "0.8em",
              visibility:
                saveDataSubmitted && getLastNameErrors(newLastName, t)
                  ? "visible"
                  : "hidden",
            }}
          >
            {getLastNameErrors(newLastName, t)}
          </label>
        </div>
        <div className="mb-3" style={{ marginTop: "2vh" }}>
          <label
            htmlFor="languageSelect"
            style={{
              marginLeft: "20px",
              color: "#404040",
              fontWeight: "bold",
            }}
          >
            {t("Preferred_Language")}
          </label>
          <select
            className="form-control"
            id="languageSelect"
            value={newPreferredLanguage}
            onChange={(e) => setNewPreferredLanguage(e.target.value)}
            style={{
              backgroundColor: "#CCCCCC",
              borderRadius: "20px",
              color: "#404040",
              height: "5vh",
              maxHeight: "40px",
              fontSize: "1em",
              paddingLeft: "20px",
            }}
          >
            <option value="EN">{t("English")}</option>
            <option value="DE">{t("German")}</option>
            <option value="CZ">{t("Czech")}</option>
          </select>
        </div>
        {(adminView || headquarterView || groupView) && (
          <>
            <div className="mb-3" style={{ marginTop: "2vh" }}>
              <label
                htmlFor="roleSelect"
                style={{
                  marginLeft: "20px",
                  color: "#404040",
                  fontWeight: "bold",
                }}
              >
                {t("Role")}
              </label>
              <select
                id="roleSelect"
                className="form-control"
                value={newRole}
                style={{
                  backgroundColor: "#CCCCCC",
                  borderRadius: "20px",
                  color: "#404040",
                  height: "5vh",
                  maxHeight: "40px",
                  fontSize: "1em",
                  paddingLeft: "20px",
                }}
                onChange={handleRoleChange}
              >
                {adminView && <option value="guest">{t("Guest")}</option>}
                {(adminView || headquarterView || groupView) && (
                  <>
                    <option value="user">{t("Group_User")}</option>
                    <option value="groupAdmin">{t("Group_Administrator")}</option>
                  </>
                )}
                {(adminView || headquarterView) && (
                  <option value="headquarterAdmin">{t("Headquarter_Administrator")}</option>
                )}
              </select>
            </div>
          </>
        )}

        {(newRole === UserRole.User || newRole === UserRole.GroupAdmin) &&
          (adminView || headquarterView) && (
            <>
              <div className="mb-3" style={{ marginTop: "2vh" }}>
                <label
                  htmlFor="groupSelect"
                  style={{
                    marginLeft: "20px",
                    color: "#404040",
                    fontWeight: "bold",
                  }}
                >
                  {t("Group")}
                </label>
                <select
                  id="groupSelect"
                  className="form-control"
                  value={newGroupId}
                  style={{
                    backgroundColor: "#CCCCCC",
                    borderRadius: "20px",
                    color: "#404040",
                    height: "5vh",
                    maxHeight: "40px",
                    fontSize: "1em",
                    paddingLeft: "20px",
                  }}
                  onChange={(e) => {
                    const selectedGroupId = e.target.value;
                    const selectedGroup = groups.find(
                      (group) => group.id === selectedGroupId
                    );
                    if (selectedGroup) {
                      const selectedGroupCountry = countries.find(
                        (country) => country.id === selectedGroup.country
                      );

                      setNewGroupId(selectedGroupId);
                      setGroupName(selectedGroup.name);
                      setGroupCountry(selectedGroupCountry.name);
                      setHeadquarterName(selectedGroup.headquarterName);
                      setSaveDataSubmitted(false);
                    }
                  }}
                >
                  <option value=""></option>
                  {groups.map((group: any) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
                <label
                  htmlFor="groupSelect"
                  style={{
                    marginLeft: "20px",
                    color: "#A63939",
                    fontSize: "0.8em",
                    visibility:
                      saveDataSubmitted &&
                      newGroupId === "" &&
                      (newRole === UserRole.User ||
                        newRole === UserRole.GroupAdmin)
                        ? "visible"
                        : "hidden",
                  }}
                >
                  {t("Group_is_required")}
                </label>
              </div>
            </>
          )}

        {newRole === UserRole.HeadquarterAdmin && adminView && (
          <>
            <div className="mb-3" style={{ marginTop: "2vh" }}>
              <label
                htmlFor="headquarterSelect"
                style={{
                  marginLeft: "20px",
                  color: "#404040",
                  fontWeight: "bold",
                }}
              >
                {t("Headquarter")}
              </label>
              <select
                id="headquarterSelect"
                className="form-control"
                value={newHeadquarterId}
                style={{
                  backgroundColor: "#CCCCCC",
                  borderRadius: "20px",
                  color: "#404040",
                  height: "5vh",
                  maxHeight: "40px",
                  fontSize: "1em",
                  paddingLeft: "20px",
                }}
                onChange={(e) => {
                  const selectedHeadquarterId = e.target.value;
                  const selectedHeadquarter = headquarters.find(
                    (headquarter) => headquarter.id === selectedHeadquarterId
                  );
                  if (selectedHeadquarter) {
                    setNewHeadquarterId(selectedHeadquarterId);
                    setHeadquarterName(selectedHeadquarter.name);
                    setSaveDataSubmitted(false);
                  }
                }}
              >
                <option value=""></option>
                {headquarters.map((headquarter: any) => (
                  <option key={headquarter.id} value={headquarter.id}>
                    {headquarter.name}
                  </option>
                ))}
              </select>
              <label
                htmlFor="headquarterSelect"
                style={{
                  marginLeft: "20px",
                  color: "#A63939",
                  fontSize: "0.8em",
                  visibility:
                    saveDataSubmitted &&
                    newHeadquarterId === "" &&
                    newRole === UserRole.HeadquarterAdmin
                      ? "visible"
                      : "hidden",
                }}
              >
                {t("Headquarter_is_required")}
              </label>
            </div>
          </>
        )}

        <div className="button-container">
          <button
            type="button"
            className="buttonCancel btn btn-primary"
            onClick={() => {
              setShowChangeDataModal(false);
              setSaveDataSubmitted(false);
            }}
          >
            {t("Cancel")}
          </button>
          <button
            type="button"
            className="buttonAcceptGreen btn btn-primary"
            onClick={() => handleSaveDataClick()}
          >
            {t("Save")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeUserDataModal;
